import { Link } from "react-router-dom";
import { useBrand } from "./useBrand";

/**
 * 404 for a URL inside a membership — a section slug this app doesn't have.
 *
 * Separate from App.js's NoCommunity, which answers a different question: that
 * one fires when there is no membership token in the URL at all and tells the
 * visitor to add one. Showing it here would advise someone to fix a part of the
 * URL that is already correct.
 *
 * `token` is optional: with one we can offer the way back into that membership,
 * without one the page still renders rather than throwing.
 */
export default function NotFound({ token, section }) {
  // The membership's own colour when we know which membership we're in, so a
  // wrong URL still looks like part of the product rather than a bare error.
  // useBrand always resolves a primary (its own default when the token is
  // unknown or the fetch fails), so there is nothing to fall back to here.
  const { primary } = useBrand(token);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-50 to-surface px-6 py-16">
      <div className="flex w-full max-w-md flex-col items-center text-center">
        <span
          className="flex h-16 w-16 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${primary}12`, color: primary }}
        >
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </span>

        <p className="mt-5 text-[13px] font-bold uppercase tracking-[0.14em] text-gray-400">
          Error 404
        </p>
        <h1 className="mt-2 text-balance text-[24px] font-extrabold leading-snug tracking-[-0.01em] text-gray-900 sm:text-[28px]">
          This page doesn't exist
        </h1>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-gray-500">
          {section ? (
            <>
              There's no <span className="font-semibold text-gray-700">“{section}”</span> section in this
              membership. Check the address, or head back to your courses.
            </>
          ) : (
            "Check the address, or head back to your courses."
          )}
        </p>

        {token && (
          <Link
            to={`/membership/${token}/courses`}
            className="mt-7 inline-flex items-center gap-1.5 rounded-xl px-6 py-3 text-[14.5px] font-bold transition-all duration-200 hover:brightness-[1.06] active:scale-[.98]"
            style={{
              background: `linear-gradient(140deg, ${primary}, ${primary}c4)`,
              color: "var(--brand-btn-text, #fff)",
              boxShadow: `0 14px 28px -14px ${primary}`,
            }}
          >
            Back to courses
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h11" />
            </svg>
          </Link>
        )}
      </div>
    </div>
  );
}
